/**
 * Categories List Component
 * 
 * Main container for categories list with search, filter, sort, and bulk operations
 * Following the design specifications and accounts feature pattern
 * 
 * Features:
 * - Hierarchical tree display
 * - Search, filter, and sort functionality
 * - Bulk operations
 * - Add/Edit/Delete category modals
 * - WCAG 2.2 AA compliant
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { categoryService } from '../../../../services/category-service';
import { categorySeedingService } from '../../../../services/category-seeding-service';
import { Category, CategoryType } from '../../../../types/category.types';
import { Plus, Download, FolderTree } from 'lucide-react';
import { AddCategoryModal } from './AddCategoryModal';
import { EditCategoryModal } from './EditCategoryModal';
import { CategoryTree } from './CategoryTree';
import { SearchBar } from './SearchBar';
import { FilterControls } from './FilterControls';
import { BulkActionsToolbar } from './BulkActionsToolbar';
import { useSearchParams } from 'react-router-dom';

export function CategoriesList(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const getInitialInsightsFilter = (): 'all' | 'included' | 'excluded' => {
    const param = (searchParams.get('insights') || 'all').toLowerCase();
    if (param === 'included' || param === 'excluded') {
      return param;
    }
    return 'all';
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<CategoryType | 'all'>('all');
  const [filterInsights, setFilterInsights] = useState<'all' | 'included' | 'excluded'>(getInitialInsightsFilter);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);

  // Fetch categories
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['categories', searchTerm, filterType, filterInsights],
    queryFn: async () => {
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      if (filterType !== 'all') params.type = filterType;
      if (filterInsights === 'included') params.excludeFromSpending = false;
      if (filterInsights === 'excluded') params.excludeFromSpending = true;
      
      const result = await categoryService.listCategories(params);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    }
  });

  const allCategories = data?.categories || [];

  // Sort categories by name (alphabetically) while maintaining hierarchy
  // IMPORTANT: This hook must be called before any early returns to maintain hook order
  const sortedCategories = useMemo(() => {
    if (!allCategories.length) return [];

    // Maintain hierarchy: sort root categories, then sort children within each parent
    const rootCategories: Category[] = [];
    const childrenByParent = new Map<string, Category[]>();

    // Build maps
    allCategories.forEach(cat => {
      if (!cat.parentId) {
        rootCategories.push(cat);
      } else {
        if (!childrenByParent.has(cat.parentId)) {
          childrenByParent.set(cat.parentId, []);
        }
        childrenByParent.get(cat.parentId)!.push(cat);
      }
    });

    // Sort root categories by name
    rootCategories.sort((a, b) => a.name.localeCompare(b.name));

    // Sort children for each parent by name
    childrenByParent.forEach((children) => {
      children.sort((a, b) => a.name.localeCompare(b.name));
    });

    // Rebuild flat array maintaining hierarchy order
    const result: Category[] = [];
    rootCategories.forEach(root => {
      result.push(root);
      const children = childrenByParent.get(root.id) || [];
      result.push(...children);
    });

    return result;
  }, [allCategories]);

  const categories = sortedCategories;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const queryClient = useQueryClient();

  const handleAddCategory = () => {
    setShowAddModal(true);
  };

  const handleSeedCategories = async () => {
    if (window.confirm('This will import 175 predefined Australian categories. Continue?')) {
      setIsSeeding(true);
      try {
        const result = await categorySeedingService.seedSystemCategories();
        if (result.error) {
          alert(`Error seeding categories: ${result.error.message}`);
        } else {
          alert(`Successfully imported ${result.data?.length || 0} categories!`);
          queryClient.invalidateQueries({ queryKey: ['categories'] });
        }
      } catch (error) {
        alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setIsSeeding(false);
      }
    }
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
  };

  const handleDeleteCategory = async (category: Category) => {
    if (window.confirm(`Are you sure you want to delete "${category.name}"?`)) {
      setIsSubmitting(true);
      const result = await categoryService.deleteCategory(category.id);
      setIsSubmitting(false);
      if (!result.error) {
        refetch();
      } else {
        alert(result.error.message);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCategories.size === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedCategories.size} categories?`)) {
      setIsSubmitting(true);
      const result = await categoryService.bulkDeleteCategories(Array.from(selectedCategories));
      setIsSubmitting(false);
      if (!result.error) {
        setSelectedCategories(new Set());
        refetch();
      } else {
        alert(result.error.message);
      }
    }
  };

  const handleBulkEdit = () => {
    // TODO: Implement bulk edit modal
    console.log('Bulk edit categories:', Array.from(selectedCategories));
  };

  const handleBulkMove = (parentId: string) => {
    // TODO: Implement bulk move modal
    console.log('Bulk move categories:', Array.from(selectedCategories), 'to parent:', parentId);
  };

  const handleAddCategorySubmit = async (data: any) => {
    setIsSubmitting(true);
    const result = await categoryService.createCategory(data);
    setIsSubmitting(false);
    if (!result.error) {
      setShowAddModal(false);
      refetch();
    } else {
      alert(result.error.message);
    }
  };

  const handleEditCategorySubmit = async (data: any) => {
    if (!editingCategory) return;
    setIsSubmitting(true);
    const result = await categoryService.updateCategory(editingCategory.id, data);
    setIsSubmitting(false);
    if (!result.error) {
      setEditingCategory(null);
      refetch();
    } else {
      alert(result.error.message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 dark:text-gray-400">Loading categories...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">
          Error loading categories: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  const hasSystemCategories = allCategories.some(cat => cat.isSystem);

  return (
    <div className="space-y-6">
      {/* Category Structure Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <FolderTree className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Category Structure
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Organize and manage your income and expense categories
          </p>
        </div>

        {/* Header Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            {!hasSystemCategories && (
              <button
                onClick={handleSeedCategories}
                disabled={isSeeding}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium text-sm shadow-sm hover:shadow-md transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Import system categories"
              >
                <Download className="w-4 h-4" aria-hidden="true" />
                <span>{isSeeding ? 'Importing...' : 'Import Categories'}</span>
              </button>
            )}
            <button
              onClick={handleAddCategory}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium text-sm shadow-sm hover:shadow-md transition-all duration-200 flex items-center space-x-2"
              aria-label="Add new category"
            >
              <Plus className="w-4 h-4" aria-hidden="true" />
              <span>Add Category</span>
            </button>
          </div>
        </div>

        {/* Search, Filter, and Sort Controls */}
        <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search categories..."
          />
        </div>
        <FilterControls
          filters={{ type: filterType, insights: filterInsights }}
          onFilterChange={(filters) => {
            setFilterType(filters.type || 'all');
            const insightsValue = (filters.insights ?? 'all') as typeof filterInsights;
            setFilterInsights(insightsValue);
          }}
          availableFilters={[
            { key: 'type', label: 'Type', options: [
              { value: 'all', label: 'All' },
              { value: CategoryType.INCOME, label: 'Income' },
              { value: CategoryType.EXPENSE, label: 'Expense' },
              { value: CategoryType.TRANSFER, label: 'Transfer' }
            ]},
            { key: 'insights', label: 'Spending Insights', options: [
              { value: 'all', label: 'All categories' },
              { value: 'included', label: 'Included in insights' },
              { value: 'excluded', label: 'Excluded from insights' }
            ]}
          ]}
        />
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedCategories.size > 0 && (
        <BulkActionsToolbar
          selectedCount={selectedCategories.size}
          onBulkDelete={handleBulkDelete}
          onBulkEdit={handleBulkEdit}
          onBulkMove={handleBulkMove}
          onClearSelection={() => setSelectedCategories(new Set())}
          availableActions={['delete', 'edit', 'move']}
        />
      )}

      {/* Categories Tree */}
      <div className="overflow-hidden -mx-6 -mb-6 mt-6">
        <CategoryTree
          categories={categories}
          onEdit={handleEditCategory}
          onDelete={handleDeleteCategory}
          selectedIds={selectedCategories}
          onSelectionChange={setSelectedCategories}
        />
      </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddCategoryModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddCategorySubmit}
          categories={categories}
          loading={isSubmitting}
        />
      )}

      {editingCategory && (
        <EditCategoryModal
          category={editingCategory}
          categories={categories}
          onClose={() => setEditingCategory(null)}
          onSubmit={handleEditCategorySubmit}
          loading={isSubmitting}
        />
      )}
    </div>
  );
}

